import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Logo from '../common/Logo';
import '../salon/styles/base.css'; // Import global form styles
import './Auth.css';

const Login = () => {
  const { t, translateMessage } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setShowResendVerification(false);
    setResendSuccess('');

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      const errorMessage = result.error;
      setError(translateMessage(errorMessage));
      
      // Show resend verification option if email is not verified
      if (errorMessage === 'Please verify your email before logging in') {
        setShowResendVerification(true);
      }
    }
    
    setLoading(false);
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setError(t('messages.error.invalidEmail'));
      return;
    }

    setResendLoading(true);
    setResendSuccess('');
    setError('');

    try {
      const response = await axios.post('/api/auth/resend-verification', {
        email: formData.email
      });
      
      setResendSuccess(translateMessage(response.data.message));
      setShowResendVerification(false);
    } catch (error) {
      console.error('Error resending verification:', error);
      setError(translateMessage(error.response?.data?.error || 'Failed to resend verification email'));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <Logo />
          <p>{t('auth.loginToContinue')}</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {resendSuccess && (
          <div className="success-message">
            {resendSuccess}
          </div>
        )}

        <form onSubmit={handleSubmit} className="base-form">
          <div className="form-group">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder={t('auth.enterEmail')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.password')}</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder={t('auth.enterPassword')}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onMouseDown={() => setShowPassword(true)}
                onMouseUp={() => setShowPassword(false)}
                onMouseLeave={() => setShowPassword(false)}
                onTouchStart={() => setShowPassword(true)}
                onTouchEnd={() => setShowPassword(false)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button 
            type="submit" 
            className="salon-button primary"
            disabled={loading}
          >
            {loading ? `${t('auth.signIn')}...` : t('auth.signIn')}
          </button>
        </form>

        <div className="auth-links">
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <Link to="/forgot-password" className="link">
              {t('auth.forgotPassword')}?
            </Link>
            {showResendVerification && (
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendLoading}
                className="link"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: resendLoading ? 'default' : 'pointer',
                  opacity: resendLoading ? 0.6 : 1
                }}
              >
                {resendLoading ? `${t('auth.sending')}...` : t('auth.resendVerificationEmail')}
              </button>
            )}
          </div>
          <div className="divider">
            <span>or</span>
          </div>
          <p>
            {t('auth.dontHaveAccount')}{' '}
            <Link to="/register" className="link">
              {t('auth.signUp')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
